import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CertificationService } from '../../core/services/certification.service';
import {
  Certification,
  CertificationType,
  CERTIFICATION_TYPE_NAMES,
  CertificationCreateRequest,
  CertificationUpdateRequest
} from '../../core/models/certification.model';
import { UsStateSelect } from './app-us-state-select/app-us-state-select';
import { FileUploadService } from '../../core/services/file-upload.service';

interface FileUploadResponse {
  success: boolean;
  data?: {
    uploadUrl: string;
    fileUrl: string;
  };
  error?: string;
}

/**
 * Certification Upload Modal Component
 *
 * Modal for uploading certifications to organizations.
 * Handles file upload via S3 pre-signed URLs.
 */
@Component({
  selector: 'app-certification-upload-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, UsStateSelect],
  templateUrl: './certification-upload-modal.component.html',
  styleUrls: ['./certification-upload-modal.component.scss']
})
export class CertificationUploadModalComponent implements OnInit {
  @Input() organizationId?: string;
  @Input() profileId?: string;
  @Input() certification?: Certification;
  @Output() close = new EventEmitter<void>();
  @Output() uploaded = new EventEmitter<void>();

  get isEditMode(): boolean {
    return !!this.certification;
  }

  // Form fields
  certificationType: CertificationType = 'faa-44807';
  certificateNumber = '';
  issuedDate = '';
  expirationDate = '';
  issuingAuthority = '';
  state = '';
  notes = '';

  // File upload
  selectedFile: File | null = null;
  uploadedFileUrl = '';
  uploadedFileName = '';

  // UI state
  uploading = false;
  uploadingFile = false;
  errorMessage: string | null = null;

  certificationTypes: { value: CertificationType; label: string }[] = [];

  // Default issuing authorities by certification type
  private readonly defaultAuthorities: Partial<Record<CertificationType, string>> = {
    'faa-44807': 'FAA',
    'faa-part-107': 'FAA',
    'faa-part-108': 'FAA',
    'faa-part-137': 'FAA',
    'medical': 'FAA',
  };

  constructor(
    private certificationService: CertificationService,
    private http: HttpClient,
    private fileUploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    // Populate certification types based on owner type
    if (this.organizationId) {
      // Organization certifications
      this.certificationTypes = [
        { value: 'faa-44807', label: CERTIFICATION_TYPE_NAMES['faa-44807'] },
        { value: 'faa-part-137', label: CERTIFICATION_TYPE_NAMES['faa-part-137'] },
        { value: 'insurance', label: CERTIFICATION_TYPE_NAMES['insurance'] },
        { value: 'medical', label: CERTIFICATION_TYPE_NAMES['medical']},
        { value: 'state-applicator', label: CERTIFICATION_TYPE_NAMES['state-applicator'] },
        { value: 'other', label: CERTIFICATION_TYPE_NAMES['other'] }
      ];
    } else if (this.profileId) {
      // Member certifications
      this.certificationTypes = [
        { value: 'faa-part-107', label: CERTIFICATION_TYPE_NAMES['faa-part-107'] },
        { value: 'faa-part-108', label: CERTIFICATION_TYPE_NAMES['faa-part-108'] },
        { value: 'medical', label: CERTIFICATION_TYPE_NAMES['medical'] },
        { value: 'state-applicator', label: CERTIFICATION_TYPE_NAMES['state-applicator'] },
        { value: 'other', label: CERTIFICATION_TYPE_NAMES['other'] }
      ];
      this.certificationType = 'faa-part-107';
    }

    // Pre-populate form in edit mode
    if (this.certification) {
      this.certificationType = this.certification.certificationType;
      this.certificateNumber = this.certification.certificateNumber;
      this.issuedDate = this.certification.issuedDate?.substring(0, 10) || '';
      this.expirationDate = this.certification.expirationDate?.substring(0, 10) || '';
      this.issuingAuthority = this.certification.issuingAuthority;
      this.state = this.certification.state || '';
      this.notes = this.certification.notes || '';
      this.uploadedFileUrl = this.certification.documentUrl || '';
      this.uploadedFileName = this.certification.documentFileName || '';
    } else {
      // Pre-fill issuing authority for the default type
      this.onCertificationTypeChange();
    }
  }

  onCertificationTypeChange(): void {
    this.issuingAuthority = this.defaultAuthorities[this.certificationType] || '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.errorMessage = 'Please upload a PDF, JPEG, or PNG file';
        this.selectedFile = null;
        return;
      }

      // Validate file size (100MB max)
      const maxSize = this.fileUploadService.getMaxFileSize();
      if (file.size > maxSize) {
        this.errorMessage = 'File size must be less than ' + this.fileUploadService.formatFileSize(maxSize);
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;
      this.errorMessage = null;
    }
  }

  async uploadFile(): Promise<boolean> {
    if (!this.selectedFile) {
      return false;
    }

    this.uploadingFile = true;
    this.errorMessage = '';

    try {
      // Step 1: Get pre-signed URL
      const uploadResponse = await firstValueFrom(
        this.http.post<FileUploadResponse>(
          `${environment.apiUrl}/admin/file-uploads/certification`,
          { filename: this.selectedFile.name }
        )
      );

      if (!uploadResponse.success || !uploadResponse.data) {
        throw new Error(uploadResponse.error || 'Failed to get upload URL');
      }

      const { uploadUrl, fileUrl } = uploadResponse.data;

      // Step 2: Upload file to S3
      await firstValueFrom(
        this.http.put(uploadUrl, this.selectedFile, {
          headers: {
            'Content-Type': this.selectedFile.type
          }
        })
      );

      // Step 3: Save file URL
      this.uploadedFileUrl = fileUrl;
      this.uploadedFileName = this.selectedFile.name;

      return true;
    } catch (error: any) {
      console.error('File upload failed:', error);
      this.errorMessage = 'Failed to upload file. Please try again.';
      return false;
    } finally {
      this.uploadingFile = false;
    }
  }

  async onSubmit(): Promise<void> {
    this.errorMessage = '';

    // Validate required fields
    if (!this.certificationType || !this.certificateNumber || !this.issuedDate || !this.issuingAuthority) {
      this.errorMessage = 'Please fill in all required fields.';
      return;
    }

    this.uploading = true;

    try {
      // Upload file if selected
      if (this.selectedFile) {
        const uploaded = await this.uploadFile();
        if (!uploaded) {
          this.uploading = false;
          return;
        }
      }

      if (this.isEditMode && this.certification) {
        // Update existing certification
        const updateRequest: CertificationUpdateRequest = {
          certificateNumber: this.certificateNumber,
          issuedDate: this.issuedDate,
          expirationDate: this.expirationDate || undefined,
          issuingAuthority: this.issuingAuthority,
          state: this.state || undefined,
          documentUrl: this.uploadedFileUrl || undefined,
          documentFileName: this.uploadedFileName || undefined,
          notes: this.notes || undefined
        };

        await firstValueFrom(
          this.certificationService.updateCertification(this.certification.id, updateRequest)
        );
      } else {
        // Create certification request
        const request: CertificationCreateRequest = {
          certificationType: this.certificationType,
          ownerType: this.organizationId ? 'organization' : 'profile',
          certificateNumber: this.certificateNumber,
          issuedDate: this.issuedDate,
          expirationDate: this.expirationDate || undefined,
          issuingAuthority: this.issuingAuthority,
          state: this.state || undefined,
          documentUrl: this.uploadedFileUrl || undefined,
          documentFileName: this.uploadedFileName || undefined,
          notes: this.notes || undefined
        };

        if (this.organizationId) {
          await firstValueFrom(
            this.certificationService.uploadOrganizationCertification(this.organizationId, request)
          );
        } else if (this.profileId) {
          await firstValueFrom(
            this.certificationService.uploadMemberCertification(this.profileId, request)
          );
        }
      }

      // Emit success and close modal
      this.uploaded.emit();
    } catch (error: any) {
      console.error('Failed to upload certification:', error);
      this.errorMessage = error.error?.error || 'Failed to upload certification. Please try again.';
    } finally {
      this.uploading = false;
    }
  }

  onClose(): void {
    this.close.emit();
  }

  get isValid(): boolean {
    return !!(
      this.certificationType &&
      this.certificateNumber &&
      this.issuedDate &&
      this.issuingAuthority
    );
  }
}
